import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Users, Shield, Clock, CheckCircle, ArrowRight, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { companyInfo, serviceAreas } from '../data/mock';
import { pageSEO } from '../data/seoData';
import SEOHead from '../components/SEOHead';

const AboutPage = () => {
  return (
    <div>
      <SEOHead 
        title={pageSEO.about.title}
        description={pageSEO.about.description}
        canonical="/about"
      />
      {/* Hero Section */}
      <section className="relative py-24 bg-neutral-900">
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url('https://images.pexels.com/photos/8447879/pexels-photo-8447879.jpeg?auto=compress&cs=tinysrgb&w=1260')` }}
        />
        <div className="relative z-10 max-w-7xl mx-auto px-4 text-center">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-6">About Gray Contracting</h1>
          <p className="text-xl text-neutral-300 max-w-3xl mx-auto">
            Dallas's trusted family-owned contractor since 1969, delivering quality craftsmanship for over 55 years.
          </p>
        </div>
      </section>

      {/* Stats Bar */}
      <section className="bg-amber-500 py-8">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center text-white">
              <div className="text-3xl font-bold">55+</div>
              <div className="text-white/80 text-sm">Years Experience</div>
            </div>
            <div className="text-center text-white">
              <div className="text-3xl font-bold">5000+</div>
              <div className="text-white/80 text-sm">Projects Completed</div>
            </div>
            <div className="text-center text-white">
              <div className="text-3xl font-bold">15+</div>
              <div className="text-white/80 text-sm">Cities Served</div>
            </div>
            <div className="text-center text-white">
              <div className="text-3xl font-bold">100%</div>
              <div className="text-white/80 text-sm">Satisfaction</div>
            </div>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Our Story</span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-6">
                55+ Years of Building Trust in Dallas
              </h2>
              <div className="space-y-4 text-neutral-600">
                <p>Gray Contracting was founded in 1969 with a simple mission: deliver exceptional craftsmanship at fair prices while treating every customer like family.</p>
                <p>As a family-owned business, we've built our reputation on trust, reliability, and consistent quality. We've watched Dallas grow and we're proud to have contributed to making homes throughout the metroplex more beautiful.</p>
                <p>Today, Gray Contracting continues to serve homeowners across Dallas-Fort Worth with the same dedication to excellence that has defined us for over half a century.</p>
              </div>
            </div>
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1600585154526-990dced4db0d?w=800&q=80" alt="Professional craftsmanship" className="rounded-2xl shadow-xl" />
              <div className="absolute -bottom-8 -right-8 bg-neutral-900 text-white p-8 rounded-xl shadow-xl hidden md:block">
                <div className="text-sm text-neutral-400 mb-2">Established</div>
                <div className="text-4xl font-bold">1969</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Values */}
      <section className="py-20 bg-neutral-50">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-16">
            <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Our Mission</span>
            <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-6">Quality Service at Affordable Prices</h2>
            <p className="text-neutral-600 max-w-3xl mx-auto text-lg">
              To deliver high-quality contracting services at affordable prices while maintaining transparency, professionalism, and long-term value.
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-8">
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Award className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 text-lg mb-3">Excellence</h3>
              <p className="text-neutral-600 text-sm">Highest standards in every project, big or small.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Users className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 text-lg mb-3">Family Values</h3>
              <p className="text-neutral-600 text-sm">Treating every client with respect, honesty, and care.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Shield className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 text-lg mb-3">Integrity</h3>
              <p className="text-neutral-600 text-sm">Transparent pricing and honest timelines you can count on.</p>
            </div>
            <div className="bg-white p-8 rounded-xl shadow-md text-center">
              <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-6">
                <Clock className="w-8 h-8 text-amber-600" />
              </div>
              <h3 className="font-semibold text-neutral-900 text-lg mb-3">Reliability</h3>
              <p className="text-neutral-600 text-sm">On time, on budget, and delivering on promises.</p>
            </div>
          </div>
        </div>
      </section>

      {/* What Sets Us Apart */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div className="relative">
              <img src="https://images.unsplash.com/photo-1591825729269-caeb344f6df2?w=800&q=80" alt="Quality deck construction" className="rounded-2xl shadow-xl" />
            </div>
            <div>
              <span className="text-amber-600 font-semibold text-sm uppercase tracking-wider">Why Choose Us</span>
              <h2 className="text-3xl md:text-4xl font-bold text-neutral-900 mt-4 mb-8">What Sets Gray Contracting Apart</h2>
              <div className="space-y-6">
                {["Five decades of experience in DFW", "Family-owned with personalized service", "Fully licensed and insured", "Lead and mold certified professionals", "Transparent pricing", "Skilled craftsmanship", "On-time completion", "100% satisfaction guarantee"].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <CheckCircle className="w-6 h-6 text-amber-500 flex-shrink-0 mt-0.5" />
                    <span className="text-neutral-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Areas */}
      <section className="py-20 bg-neutral-900 text-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="text-center mb-12">
            <span className="text-amber-400 font-semibold text-sm uppercase tracking-wider">Coverage</span>
            <h2 className="text-3xl md:text-4xl font-bold mt-4 mb-4">Serving Dallas-Fort Worth</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {serviceAreas.map((area, i) => (
              <span key={i} className="px-6 py-3 bg-neutral-800 rounded-full text-sm font-medium">{area}, TX</span>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-amber-500">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">Ready to Work Together?</h2>
          <p className="text-white/90 text-lg mb-8">Let's discuss your project. Get a free estimate today.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact">
              <Button size="lg" className="bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-8 py-6 text-lg">
                Get Free Estimate <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <a href={`tel:${companyInfo.phone}`}>
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white hover:text-amber-600 font-semibold px-8 py-6 text-lg">
                <Phone className="mr-2 w-5 h-5" />{companyInfo.phone}
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
